import React, { lazy } from 'react';

import { ReactComponent as Close } from 'assets/icons/close.svg';
import { ReactComponent as Play } from 'assets/icons/play_circle_outline.svg';
import { ReactComponent as Pause } from 'assets/icons/pause_circle_outline.svg';
import { ReactComponent as Previous } from 'assets/icons/skip_previous.svg';
import { ReactComponent as Next } from 'assets/icons/skip_next.svg';
import { ReactComponent as QueueIcon } from 'assets/icons/queue_music.svg';

import Spinner from 'components/spinner';
import Container from 'components/container';

import { useStateValue } from 'context/store';
import {
  togglePlayPause,
  openPlayerModal,
  closePlayer,
  playSong,
  setPlayed,
  setSeeking
} from 'context/player/actions';

import { formatSeconds } from 'utils/stringUtil';

import {
  MediaPlayer,
  PlayPause,
  Loader,
  Queue,
  Wrapper,
  Controls,
  Title,
  Slider,
  Track,
  Thumb,
  MediaContent
} from './elements';

const PlayerModal = lazy(() =>
  import(/* webpackChunkName: "playerModal" */ 'components/playerModal')
);

const MobileStyles = {
  overlay: {
    background: 'rgba(49, 56, 62, 0.7)',
    zIndex: 100
  },
  content: {
    width: '96%',
    borderRadius: 16,
    top: '10%',
    bottom: 'initial',
    padding: 0,
    margin: 'auto',
    left: '2px',
    right: '2px'
  }
};

function Mobile({ play, pathname }) {
  const {
    state: { player },
    dispatch
  } = useStateValue();

  const {
    song,
    playlist,
    isBuffering,
    isPlaying,
    duration,
    played,
    playedSeconds
  } = player;

  const [author] = song.authors;

  const nextSong = song.trackNumber + 1;
  const previousSong = song.trackNumber - 1;

  function handleSeekTouchStart() {
    dispatch(setSeeking(true));
  }

  function handleSeekChange(e) {
    dispatch(setPlayed(parseFloat(e.target.value)));
  }

  function handleSeekTouchEnd(e) {
    play.current.seekTo(parseFloat(e.target.value));
    dispatch(setSeeking(false));
  }

  function handlePlaySong(songType) {
    const song = playlist.filter(track => track.trackNumber === songType);
    dispatch(playSong(...song));
  }

  function toggleButtons() {
    return isPlaying ? <Pause /> : <Play />;
  }

  return (
    <Wrapper embed={pathname.includes('embed')}>
      <MediaPlayer>
        <Container>
          <MediaContent style={{ marginBottom: 7 }}>
            <div style={{ display: 'flex' }}>
              {previousSong >= 0 ? (
                <Previous onClick={() => handlePlaySong(previousSong)} />
              ) : (
                <Previous style={{ opacity: '0.4', cursor: 'initial' }} />
              )}
              {isBuffering ? (
                <Loader>
                  <Spinner />
                </Loader>
              ) : (
                <PlayPause onClick={() => dispatch(togglePlayPause())}>
                  {toggleButtons()}
                </PlayPause>
              )}
              {nextSong < playlist.length ? (
                <Next onClick={() => handlePlaySong(nextSong)} />
              ) : (
                <Next style={{ opacity: '0.4', cursor: 'initial' }} />
              )}
            </div>
            <div style={{ display: 'flex' }}>
              <Queue>
                <QueueIcon onClick={() => dispatch(openPlayerModal())} />
                <PlayerModal styles={MobileStyles} />
              </Queue>
              <Close onClick={() => dispatch(closePlayer())} />
            </div>
          </MediaContent>
          <Controls>
            <Title>
              <p>
                {song.name} {author && `- ${author}`}
              </p>
              <p>
                {formatSeconds(playedSeconds)} / {formatSeconds(duration)}
              </p>
            </Title>
            <Slider>
              <Thumb
                type="range"
                min={0}
                max={1}
                step="any"
                value={played}
                onChange={handleSeekChange}
                onTouchStart={handleSeekTouchStart}
                onTouchEnd={handleSeekTouchEnd}
              />
              <Track max={1} value={played} />
            </Slider>
          </Controls>
        </Container>
      </MediaPlayer>
    </Wrapper>
  );
}

export default Mobile;
